/**
 * SchemaForm — render a form from a (subset of) JSON Schema.
 *
 * Supported v1:
 *   - string (input, or textarea via `format: 'textarea'`)
 *   - number / integer (with min/max, step inferred from type)
 *   - boolean (checkbox)
 *   - enum (select; works for any underlying type)
 *   - attachment (file upload via `x-linqage-attachment: true`; requires `onUploadAttachment`)
 *
 * Unsupported (rendered as a blocking error so the consumer knows to handle separately):
 *   - any other JSON Schema type (object, array, etc.)
 *
 * Validation is delegated to react-hook-form's built-in `register` rules (required,
 * min, max) plus `valueAsNumber` for numerics. Full JSON Schema validation should
 * happen on the server (AJV) — this primitive focuses on UX, not exhaustive validation.
 */

import { useForm, FormProvider } from 'react-hook-form';
import { TextField } from './fields/TextField';
import { NumberField } from './fields/NumberField';
import { BooleanField } from './fields/BooleanField';
import { EnumField } from './fields/EnumField';
import { AttachmentField } from './fields/AttachmentField';
import type { JsonSchema, JsonSchemaProperty } from './types';
import styles from './SchemaForm.module.css';

export interface SchemaFormProps {
  schema: JsonSchema;
  defaultValues?: Record<string, unknown>;
  onSubmit: (values: Record<string, unknown>) => void;
  submitLabel?: string;
  isPending?: boolean;
  /** Required when the schema contains `x-linqage-attachment` properties. */
  onUploadAttachment?: (file: File) => Promise<{ attachment_id: string }>;
  /** Optional id on the `<form>` so an external button can target it via the
   *  HTML5 `form` attribute. Pair with `hideSubmit` to put the submit button
   *  outside the form (e.g. in a header banner). */
  formId?: string;
  hideSubmit?: boolean;
}

const PRIMITIVE_TYPES = new Set(['string', 'number', 'integer', 'boolean']);

export function SchemaForm({
  schema,
  defaultValues,
  onSubmit,
  submitLabel = 'Submit',
  isPending,
  onUploadAttachment,
  formId,
  hideSubmit,
}: SchemaFormProps) {
  const props = schema.properties ?? {};
  const required = new Set(schema.required ?? []);

  const unsupported: string[] = [];
  for (const [name, sub] of Object.entries(props)) {
    if (sub['x-linqage-attachment']) continue; // handled below via AttachmentField
    const hasEnum = Array.isArray(sub.enum);
    if (!hasEnum && (sub.type === undefined || !PRIMITIVE_TYPES.has(sub.type))) {
      unsupported.push(`${name}: unsupported field type "${sub.type ?? '(none)'}"`);
    }
  }

  const methods = useForm<Record<string, unknown>>({ defaultValues: defaultValues ?? {} });

  if (unsupported.length > 0) {
    return (
      <div className={styles.root}>
        <div className={styles.unsupported}>
          {unsupported.map((m) => (
            <div key={m}>{m}</div>
          ))}
        </div>
        <button type="button" disabled className={styles.submit}>
          {submitLabel}
        </button>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        className={styles.root}
        onSubmit={(e) => {
          void methods.handleSubmit(onSubmit)(e);
        }}
      >
        {Object.entries(props).map(([name, sub]) => (
          <FieldFor
            key={name}
            name={name}
            sub={sub}
            required={required.has(name)}
            onUploadAttachment={onUploadAttachment}
          />
        ))}
        {hideSubmit ? null : (
          <button type="submit" className={styles.submit} disabled={isPending}>
            {isPending ? `${submitLabel}…` : submitLabel}
          </button>
        )}
      </form>
    </FormProvider>
  );
}

function FieldFor({
  name,
  sub,
  required,
  onUploadAttachment,
}: {
  name: string;
  sub: JsonSchemaProperty;
  required: boolean;
  onUploadAttachment?: (file: File) => Promise<{ attachment_id: string }>;
}) {
  if (sub['x-linqage-attachment']) {
    const upload =
      onUploadAttachment ?? (() => Promise.reject(new Error('onUploadAttachment not provided')));
    return <AttachmentField name={name} required={required} onUpload={upload} />;
  }
  if (Array.isArray(sub.enum)) return <EnumField name={name} sub={sub} required={required} />;
  if (sub.type === 'string') return <TextField name={name} sub={sub} required={required} />;
  if (sub.type === 'number' || sub.type === 'integer')
    return <NumberField name={name} sub={sub} required={required} />;
  if (sub.type === 'boolean') return <BooleanField name={name} sub={sub} required={required} />;
  return null;
}
